import { createTheme } from '@material-ui/core/styles';
import { space, themeLight, mainBoxShadow, smallBoxShadow, ColorsPaletteLight } from 'ui-components';
import { fontText, fontDisplay, ingMeWebRegular, ingMeWebBold, fontSize } from 'common-react';

const resetMuiTypographySettings = () => ({
  fontSize: undefined,
  fontFamily: undefined,
  lineHeight: undefined
})

const theme = createTheme();
const customTheme = createTheme({
  color: {
    ...themeLight.color
  },
  fontSize,
  fontFamily: {
    ingMeWebRegular,
    ingMeWebBold
  },
  boxShadow: {
    main: mainBoxShadow,
    small: smallBoxShadow,
  },
  props: {
    MuiButtonBase: {
      //Disable ripple, on the whole application
      disableRipple: true,
    },
  },
  space: {
    appPadding: space.appPadding,
    navCollapseWidth: space.navCollapseWidth,
    navWidth: space.navWidth,
    maxWidth: space.maxWidth,
    singleColumnMaxWidth: space.singleColumnMaxWidth,
    allocationProcessMaxWidth: space.allocationProcessMaxWidth,
    fundDetailsMaxWidthDefault: space.fundDetailsMaxWidthDefault,
    basicDataMaxWidth: space.basicDataMaxWidth,
    fundDetailsBackgroundMaxWidth: space.fundDetailsBackgroundMaxWidth,
    bottomBarMaxWidth: space.bottomBarMaxWidth,
    headerHeight: space.headerHeight,
    headerMobileHeight: space.headerMobileHeight,
    mobileNavHeight: space.mobileNavHeight,
    mobileBottomPadding: space.mobileBottomPadding,
    mobileTopPadding: space.mobileTopPadding,
    layoutMaxWidth: space.layoutMaxWidth,
  },
  variables: {
    isDarkTheme: false,
  },
  typography: {
    fontSize: 16,
    h1: {
      ...resetMuiTypographySettings(),
      font: fontText('normal', fontSize.xxl, 40),
      color: ColorsPaletteLight.greys[80],
      textAlign: 'center',
      letterSpacing: 'unset',
      [theme.breakpoints.down('md')]: {
        padding: '0 50px',
        margin: '0 auto',
        font: fontText('normal', fontSize.xl, 32),
      },
    },
    h4: {
      color: ColorsPaletteLight.greys[80],
      letterSpacing: 'unset',
    },
    h5: {
      ...resetMuiTypographySettings(),
      font: fontDisplay('normal', 48, 58),
      lineHeight: '100%',
      color: ColorsPaletteLight.greys[80],
    },
    h6: {
      ...resetMuiTypographySettings(),
      font: fontDisplay('normal', fontSize.s, 16),
      color: ColorsPaletteLight.greys[80],
      textTransform: 'capitalize',
      letterSpacing: 'unset',
    },
    subtitle1: {
      ...resetMuiTypographySettings(),
      font: fontText('normal', fontSize.base, 20),
      color: ColorsPaletteLight.greys[60],
      textAlign: 'center',
      letterSpacing: 'unset',
    },
    body1: {
      ...resetMuiTypographySettings(),
      font: fontText('normal', fontSize.base, 20),
      color: ColorsPaletteLight.greys[80],
      letterSpacing: 'unset',
    },
    body2: {
      ...resetMuiTypographySettings(),
      font: fontText('normal', fontSize.s, 18),
      color: ColorsPaletteLight.greys[80],
      letterSpacing: 'unset',
    },
    caption: {
      ...resetMuiTypographySettings(),
      font: fontText('normal', fontSize.s, 20),
      color: ColorsPaletteLight.greys[70],
      textAlign: 'center',
      letterSpacing: 'unset',
    },
    overline: {
      ...resetMuiTypographySettings(),
      font: fontText('normal', fontSize.s, 16),
      color: ColorsPaletteLight.greys[70],
      textTransform: 'unset',
      letterSpacing: 'unset',
    },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '*': {
          '-webkit-tap-highlight-color': 'transparent',
        },
        body: {
          backgroundColor: ColorsPaletteLight.greys[10],
          margin: 0,
          padding: 0,
          '*,  *:before, *:after': {
            boxSizing: 'border-box',
            margin: 0,
            padding: 0,
            '-webkit-tap-highlight-color': 'transparent',
          },
        },
      },
    },
    MuiContainer: {
      root: {
        marginLeft: 'auto',
        marginRight: 'auto',
        [theme.breakpoints.up('xs')]: {
          paddingLeft: '0',
          paddingRight: '0',
        },
      }
    },
    MuiToolbar: {
      gutters: {
        [theme.breakpoints.down('xl')]: {
          padding: 0,
        },
      },
    },
    MuiButton: {
      root: {
        fontFamily: ingMeWebRegular,
        display: 'block',
        margin: 0,
        borderRadius: 0,
        border: 0,
        width: '100%',
        boxShadow: 'none',
        textTransform: 'none',
        minWidth: 'unset',
        color: ColorsPaletteLight.violets[50],
        '&:hover': {
          backgroundColor: 'unset',
        },
        '&.MuiButton-containedPrimary': {
          '& .MuiButton-label': {
            font: fontText('normal', fontSize.s, 16)
          }
        },
        '&.MuiButton-containedSizeSmall': {
          '& .MuiButton-label': {
            font: fontText('normal', fontSize.s, 16)
          }
        }
      },
      contained: {
        boxShadow: 'none',
      },
      containedPrimary: {
        padding: '20px 32px',
        backgroundColor: ColorsPaletteLight.greys[80],
        borderRadius: 2,
        margin: '0 auto',
        '&:hover': {
          backgroundColor: ColorsPaletteLight.greys[60],
          '@media(hover:none)': {
            backgroundColor: ColorsPaletteLight.greys[70],
          },
        },
        '& .MuiButton-label': {
          font: fontText('normal', fontSize.s, 16),
          '& a': {
            color: ColorsPaletteLight.greys[0],
            textDecoration: 'none',
          },
        },
      },
      text: {
        padding: 0,
        display: 'inline-block',
        width: 'unset',
      },
      textPrimary: {
        '&:hover': {
          backgroundColor: 'none',
        },
      },
      outlinedPrimary: {
        padding: 0,
        border: 0,
        '&:hover': {
          border: 0,
          backgroundColor: 'transparent',
        },
        'a&, & a': {
          color: ColorsPaletteLight.violets[50],
          background: ColorsPaletteLight.greys[0],
          borderRadius: 8,
          border: '1px solid',
          borderColor: ColorsPaletteLight.violets[50],
          textAlign: 'center',
          '&:hover': {
            color: ColorsPaletteLight.violets[10],
            border: '1px solid',
            borderColor: ColorsPaletteLight.violets[10],
          },
        },
      },
      outlinedSecondary: {
        border: `1px solid ${ColorsPaletteLight.greys[70]}`,
        borderRadius: 2,
        font: fontText('normal', fontSize.s, 16),
        color: ColorsPaletteLight.greys[70],
        '&:hover': {
          border: `1px solid ${ColorsPaletteLight.greys[70]}`,
          backgroundColor: 'transparent',
          color: ColorsPaletteLight.greys[70],
        },
        '& span': {
          lineHeight: 1,
          position: 'relative',
          top: 1,
        },
      },
      sizeSmall: {
        padding: 12,
        '& a, a&': {
          textAlign: 'center',
        },
      },
    },
    MuiFormLabel: {
      root: {
        '&.Mui-disabled': {
          zIndex: 1,
          color: ColorsPaletteLight.greys[50],
        },
        'legend&': {
          font: fontDisplay('normal', fontSize.s, 16),
          color: ColorsPaletteLight.greys[50],
          textTransform: 'uppercase',
          marginBottom: 3,
        },
        '&.Mui-focused': {
          color: ColorsPaletteLight.greys[50],
        },
      },
    },
    MuiIconButton: {
      root: {
        '&:hover': {
          backgroundColor: 'inherit!important',
        },
      }
    },
    MuiTableSortLabel: {
      root: {
        font: fontDisplay('normal', fontSize.s, 16),
        lineHeight: '100%',
        color: ColorsPaletteLight.greys[50],
        textTransform: 'uppercase',
        '&.MuiTableSortLabel-active, &:hover': {
          color: ColorsPaletteLight.greys[50],
        }
      },
      icon: {
        width: 6,
        height: 4,
      },
    },
    MuiRadio: {
      root: {
        color: ColorsPaletteLight.violets[50],
        '&.Mui-checked': {
          color: ColorsPaletteLight.violets[50],
        },
      },
      colorPrimary: {
        '&.Mui-checked': {
          color: ColorsPaletteLight.violets[50],
        },
      },
    },
    MuiInputBase: {
      root: {
        padding: '11px 16px',
        border: '1px solid',
        borderColor: ColorsPaletteLight.greys[20],
        borderRadius: 2,
        '& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button': {
          appearance: 'none',
          margin: 0,
        },
        '& input[type=number]': {
          '-moz-appearance': 'textfield',
        },
        '&:hover': {
          '&:before': {
            content: '""',
            borderBottom: 0,
          },
        },
        '&.Mui-disabled': {
          color: ColorsPaletteLight.greys[70],
          backgroundColor: ColorsPaletteLight.greys[20]
        }
      },
      input: {
        padding: 0,
      },
    },
    MuiInput: {
      root: {
        font: fontText('normal', fontSize.s, 17),
        color: ColorsPaletteLight.greys[70],
        '& .Mui-focused': {
          '&:hover': {
            '&:before': {
              content: '""',
              borderBottom: 0,
            },
          },
        },
      },
      underline: {
        '&:before, &:after': {
          content: '""',
          borderBottom: '0 !important',
        },
        '&:hover:not(.Mui-disabled)': {
          '&:before': {
            content: '""',
            borderBottom: 0,
          },
        },
      },
    },
    MuiPaper: {
      elevation1: {
        boxShadow: 'none',
      },
      elevation8: {
        boxShadow: 'none',
      },
    },
    MuiChip: {
      root: {
        backgroundColor: ColorsPaletteLight.greys[20],
        border: '1px solid',
        borderColor: ColorsPaletteLight.greys[20],
        borderRadius: 20,
        height: 'auto',
        marginRight: 8,
        marginBottom: 8,
      },
      label: {
        font: fontText('normal', fontSize.s, 16),
        color: ColorsPaletteLight.greys[70],
        padding: 7,
        paddingLeft: 16,
        paddingRight: 8,
        whiteSpace: 'pre-wrap',
        position: 'relative',
        top: 1,
      },
      deleteIcon: {
        width: 16,
        height: 16,
        margin: 0,
        marginRight: 16,
      },
      deletable: {
        '&:focus': {
          backgroundColor: ColorsPaletteLight.greys[20],
        },
      },
    },

    MuiSlider: {
      root: {
        color: ColorsPaletteLight.violets[50],
        height: 4,
      },
      thumb: {
        height: 20,
        width: 20,
        backgroundColor: ColorsPaletteLight.violets[50],
        border: 'none',
        marginTop: -9,
        marginLeft: -10,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
        '&:focus,&:hover,&$active': {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
        },
      },
      active: {},
      valueLabel: {
        left: 'calc(-50% + 4px)',
      },
      track: {
        height: 3,
      },
      rail: {
        opacity: 1,
        height: 1,
        backgroundColor: ColorsPaletteLight.greys[15],
      },
    },
    MuiLinearProgress: {
      colorPrimary: {
        backgroundColor: ColorsPaletteLight.greys[20],
      },
      bar1Determinate: {
        background: ColorsPaletteLight.violets[50],
      },
      barColorPrimary: {
        background: ColorsPaletteLight.violets[50],
      },
    },
    MuiMenu: {
      paper: {
        borderRadius: 8,
        border: '1px solid',
        borderColor: ColorsPaletteLight.greys[20],
      },
    },
    MuiMenuItem: {
      root: {
        opacity: 1,
        '&.Mui-selected, &.Mui-selected:hover, &:hover': {
          backgroundColor: 'unset',
        },
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 'unset',
      },
    },
    MuiDialog: {
      paper: {
        [theme.breakpoints.down('md')]: {
          margin: 0,
        },
      },
      paperFullWidth: {
        maxWidth: '100%',
      },
      paperFullScreen: {
        '& .MuiPaper-elevation1': {
          padding: '0 16px',
          boxShadow: 'none',
          marginTop: 20,
          '& .MuiFormControl-root': {
            maxWidth: 'unset',
            width: '100%',
          },
        },
      },
      paperScrollPaper: {
        maxHeight: 'calc(100% - 45px)',
        top: -12,
      },
      paperScrollBody: {
        maxWidth: '100%',
        [theme.breakpoints.down('xs')]: {
          maxWidth: '100%!important',
        },
      },
    },
    MuiAppBar: {
      root: {
        height: 48,
        boxShadow: 'none',
      },
      colorPrimary: {
        backgroundColor: ColorsPaletteLight.greys[20],
      },
    },
    MuiStepper: {
      root: {
        maxWidth: 960,
        margin: '0 auto',
        justifyContent: 'space-between',
        [theme.breakpoints.down('sm')]: {
          paddingBottom: 0,
        },
        [theme.breakpoints.down('xs')]: {
          padding: 16,
          paddingBottom: 0,
        },
      },
      horizontal: {
        alignItems: 'flex-start',
      },
    },
    MuiStep: {
      root: {
        position: 'relative',
        maxWidth: 130,
        width: 130,
      },
    },
    MuiStepLabel: {
      root: {
        flexDirection: 'column',
      },
      labelContainer: {
        marginTop: 24,
        [theme.breakpoints.down('xs')]: {
          marginTop: 12,
        },
      },
      iconContainer: {
        paddingRight: 0,
      },
      label: {
        font: fontDisplay('normal', fontSize.s, 16),
        textAlign: 'center',
        textTransform: 'uppercase',
        color: ColorsPaletteLight.greys[20],
        '&.MuiStepLabel-completed': {
          color: ColorsPaletteLight.greys[50],
        },
        '&.MuiStepLabel-active': {
          fontFamily: ingMeWebBold,
          color: ColorsPaletteLight.violets[50],
        },
      },
      active: {
        color: ColorsPaletteLight.violets[50],
        fontWeight: 400,
      }
    },
    MuiStepConnector: {
      root: {
        display: 'none',
      },
    },
    MuiStepIcon: {
      root: {
        width: 36,
        height: 36,
        padding: 6,
        border: `1px solid ${ColorsPaletteLight.greys[50]}`,
        borderRadius: 50,
        color: ColorsPaletteLight.greys[50],
        opacity: 0.12,
      },
      completed: {
        color: ColorsPaletteLight.violets[50],
        fill: ColorsPaletteLight.oranges[50],
        borderColor: ColorsPaletteLight.oranges[50],
        opacity: 1,
      },
      active: {
        fill: ColorsPaletteLight.oranges[50],
        borderColor: ColorsPaletteLight.violets[50],
        opacity: 1,
        '& .MuiStepIcon-text': {
          fill: ColorsPaletteLight.violets[50],
        }
      }
    },
    MuiTable: {
    },
    MuiTableCell: {
      root: {
        border: 0,
        borderBottom: 0,
        padding: 0,
      },
    },
  },
});

export default customTheme;
